// Risk Pool Component
// Main component for viewing and managing the risk pool using FlexibleCRUD

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { ViewModal } from '@/components/common/ViewModal';
import { EditModal } from '@/components/common/EditModal';
import { RiskDetailView } from './RiskDetailView';
import { RiskAssignmentModal } from './RiskAssignmentModal';
import { riskEntity } from '@/config/entities/risks';
import { riskCategoryEntity } from '@/config/entities/riskCategories';
import { riskRegisterEntity } from '@/config/entities/riskRegisters';
import { createCRUDService } from '@/services/genericCRUD';
import { riskPoolService } from '@/services/riskPoolService';
import { useLanguage } from '../../state/language/LanguageProvider';
import api from '@/services/api';
import { RiskCard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { useCardViewRefresh } from '@/hooks/useCardViewRefresh';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

interface RiskPoolProps {
  onRiskSelect?: (risk: any) => void;
  onBulkAssign?: (riskIds: string[], ownerId: string) => void;
  viewMode?: 'pool' | 'evaluation' | 'review' | 'escalation' | 'closure';
  autoOpenCreate?: boolean;
  onCreateModalClosed?: () => void;
}

export const RiskPool: React.FC<RiskPoolProps> = ({ onRiskSelect, viewMode = 'pool', autoOpenCreate = false, onCreateModalClosed }) => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);
  const [viewingItem, setViewingItem] = useState<any>(null);
  const [assigningRisk, setAssigningRisk] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Card view state - default to cards
  const [displayViewMode, setDisplayViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('risk-pool-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved as ViewMode : 'cards';
  });
  const [cardData, setCardData] = useState<any[]>([]);
  const [filteredCardData, setFilteredCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('name');
  const [categories, setCategories] = useState<any[]>([]);
  const [riskCategories, setRiskCategories] = useState<{ id: string; name: string }[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [registers, setRegisters] = useState<any[]>([]);
  const [businessHierarchies, setBusinessHierarchies] = useState<any[]>([]);
  const [functionalHierarchies, setFunctionalHierarchies] = useState<any[]>([]);
  const [geographicHierarchies, setGeographicHierarchies] = useState<any[]>([]);
  const [roadmaps, setRoadmaps] = useState<any[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [transformations, setTransformations] = useState<any[]>([]);
  const [processes, setProcesses] = useState<any[]>([]);
  const [kpis, setKpis] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  
  // Use generic card view refresh hook
  const { refreshKey, refresh } = useCardViewRefresh(displayViewMode);

  // Delete confirmation dialog state (shared for card and table views)
  const [riskToDelete, setRiskToDelete] = useState<any | null>(null);
  const [skipDeleteConfirmation, setSkipDeleteConfirmation] = useState(false);

  // Use Custom Risk Pool Service for better hierarchy data
  const categoryService = createCRUDService(riskCategoryEntity);
  const registerService = createCRUDService(riskRegisterEntity);

  // Build API filters from active chips + selected categories (for table view)
  const poolFiltersFromState = React.useMemo(() => {
    const severityMap: Record<string, string> = {
      critical: 'CRITICAL',
      high: 'HIGH',
      medium: 'MEDIUM',
      low: 'LOW'
    };
    const probabilityMap: Record<string, string> = {
      'prob-low': 'LOW',
      'prob-medium': 'MEDIUM',
      'prob-high': 'HIGH'
    };
    const statusMap: Record<string, string> = {
      identified: 'IDENTIFIED',
      assigned: 'ASSIGNED',
      accepted: 'ACCEPTED',
      'mitigation-in-progress': 'MITIGATION_IN_PROGRESS',
      'mitigation-completed': 'MITIGATION_COMPLETED'
    };
    const severities = activeFilters.map((id) => severityMap[id]).filter(Boolean);
    const probabilities = activeFilters.map((id) => probabilityMap[id]).filter(Boolean);
    const statuses = activeFilters.map((id) => statusMap[id]).filter(Boolean);
    const filters: Record<string, unknown> = {};
    if (severities.length > 0) filters.severity = severities.length === 1 ? severities[0] : severities;
    if (probabilities.length > 0) filters.probability = probabilities.length === 1 ? probabilities[0] : probabilities;
    if (statuses.length > 0) filters.risk_status = statuses.length === 1 ? statuses[0] : statuses;
    if (selectedCategoryIds.length > 0) filters.risk_category_ids = selectedCategoryIds;
    return filters;
  }, [activeFilters, selectedCategoryIds]);

  const riskServiceWithFilters = React.useMemo(
    () => ({
      ...riskPoolService,
      getAll: (params: any = {}) => {
        const { page = 1, limit = 20, sortBy, sortOrder, ...rest } = params;
        const viewFilters = getViewConfig().filters || {};
        const merged = {
          ...viewFilters,
          ...poolFiltersFromState,
          ...rest,
          search: searchTerm.trim() || rest.search,
          sortBy: sortBy || 'name',
          sortOrder: sortOrder || 'asc'
        };
        return riskPoolService.getAll({ ...merged, page, limit });
      }
    }),
    [poolFiltersFromState, searchTerm]
  );

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('risk-pool-view-mode', displayViewMode);
  }, [displayViewMode]);

  // Auto-open create modal if requested
  useEffect(() => {
    if (autoOpenCreate) {
      setShowCreateModal(true);
      // Notify parent that modal was opened
      if (onCreateModalClosed) {
        // Reset the flag after a short delay to allow modal to open
        setTimeout(() => {
          onCreateModalClosed();
        }, 100);
      }
    }
  }, [autoOpenCreate, onCreateModalClosed]);

  // Check for riskId query parameter to auto-open risk detail
  useEffect(() => {
    const riskIdParam = searchParams.get('riskId');
    if (riskIdParam && riskIdParam !== selectedRiskId) {
      setSelectedRiskId(riskIdParam);
      setShowDetailView(true);
      // Clear the query parameter after opening
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('riskId');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, selectedRiskId, setSearchParams]);

  // Load risk categories for filter chips
  useEffect(() => {
    const loadRiskCategories = async () => {
      try {
        const response = await categoryService.getAll({ page: 1, limit: 500 });
        const rows = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
        const options = rows
          .filter((row: any) => row?.id && row?.name)
          .map((row: any) => ({ id: String(row.id), name: String(row.name) }));
        setRiskCategories(options);
      } catch (error) {
        console.error('Error loading risk categories:', error);
        setRiskCategories([]);
      }
    };
    loadRiskCategories();
  }, []);

  // Load card data when in card view (re-fetch when filters change so API returns filtered list)
  useEffect(() => {
    if (displayViewMode === 'cards') {
      loadCardData();
    }
  }, [displayViewMode, refreshKey, activeFilters.join(','), selectedCategoryIds.join(',')]);

  const loadCardData = async () => {
    try {
      setCardLoading(true);
      const merged = { ...getViewConfig().filters, ...poolFiltersFromState };
      const response = await riskPoolService.getAll({ ...merged, page: 1, limit: 5000 });
      const risks = response.data || [];
      setCardData(risks);
      applyFiltersAndSort(risks, searchTerm, activeFilters, sortBy);
    } catch (error) {
      console.error('Error loading risks for card view:', error);
    } finally {
      setCardLoading(false);
    }
  };

  // Apply filters and sorting to card data
  const applyFiltersAndSort = (
    data: any[],
    search: string,
    filters: string[],
    sort: string
  ) => {
    let filtered = [...data];

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.risk_category?.toLowerCase().includes(searchLower) ||
        (item.category_name && String(item.category_name).toLowerCase().includes(searchLower)) ||
        (item.current_owner_name && String(item.current_owner_name).toLowerCase().includes(searchLower)) ||
        item.owner_name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters (level + status + category)
    const levelStatusFilters = filters.filter(f => !f.startsWith('category:'));
    const categoryFilters = filters.filter(f => f.startsWith('category:'));
    const selectedCategoryIds = categoryFilters.map(f => f.replace('category:', '')).filter(Boolean);

    if (selectedCategoryIds.length > 0) {
      filtered = filtered.filter(item => item.risk_category_id && selectedCategoryIds.includes(item.risk_category_id));
    }
    // Severity / probability / status: AND across dimensions, OR within each
    const severityChips = levelStatusFilters.filter(f => ['critical', 'high', 'medium', 'low'].includes(f));
    const probChips = levelStatusFilters.filter(f => ['prob-low', 'prob-medium', 'prob-high'].includes(f));
    const statusChips = levelStatusFilters.filter(f =>
      ['identified', 'assigned', 'accepted', 'mitigation-in-progress', 'mitigation-completed'].includes(f));
    const overdueSelected = levelStatusFilters.includes('overdue');
    const chipToSeverity: Record<string, string> = { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', low: 'LOW' };
    const chipToProb: Record<string, string> = { 'prob-low': 'LOW', 'prob-medium': 'MEDIUM', 'prob-high': 'HIGH' };
    const chipToStatus: Record<string, string> = {
      identified: 'IDENTIFIED', assigned: 'ASSIGNED', accepted: 'ACCEPTED',
      'mitigation-in-progress': 'MITIGATION_IN_PROGRESS', 'mitigation-completed': 'MITIGATION_COMPLETED'
    };

    if (severityChips.length > 0 || probChips.length > 0 || statusChips.length > 0 || overdueSelected) {
      filtered = filtered.filter(item => {
        const sev = (item.severity || item.risk_level || '').toUpperCase();
        const status = (item.risk_status || item.status || '').toUpperCase();
        const prob = (item.probability || '').toUpperCase();
        const severityMatch = severityChips.length === 0 || severityChips.some(c => (chipToSeverity[c] || '') === sev);
        const probMatch = probChips.length === 0 || probChips.some(c => (chipToProb[c] || '') === prob);
        const statusMatch = statusChips.length === 0 && !overdueSelected
          || statusChips.some(c => (chipToStatus[c] || '') === status)
          || (overdueSelected && !!item.due_date && new Date(item.due_date) < new Date());
        return severityMatch && probMatch && statusMatch;
      });
    }

    // Apply sorting
    switch (sort) {
      case 'name':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        break;
      case 'priority-high': {
        const priorityOrder: Record<string, number> = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        const sev = (r: any) => (r.severity || r.risk_level || '').toUpperCase();
        filtered.sort((a, b) => (priorityOrder[sev(b)] || 0) - (priorityOrder[sev(a)] || 0));
        break;
      }
      case 'relevance':
      default: {
        const priorityOrder: Record<string, number> = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        const sev = (r: any) => (r.severity || r.risk_level || '').toUpperCase();
        filtered.sort((a, b) => {
          const aOverdue = a.due_date && new Date(a.due_date) < new Date();
          const bOverdue = b.due_date && new Date(b.due_date) < new Date();
          if (aOverdue && !bOverdue) return -1;
          if (!aOverdue && bOverdue) return 1;
          return (priorityOrder[sev(b)] || 0) - (priorityOrder[sev(a)] || 0);
        });
        break;
      }
    }

    setFilteredCardData(filtered);
  };

  // Update filtered data when search, filters, or sort changes
  const combinedActiveFilters = React.useMemo(
    () => [...activeFilters, ...selectedCategoryIds.map(id => `category:${id}`)],
    [activeFilters, selectedCategoryIds]
  );

  useEffect(() => {
    if (displayViewMode === 'cards' && cardData.length > 0) {
      applyFiltersAndSort(cardData, searchTerm, combinedActiveFilters, sortBy);
    }
  }, [searchTerm, combinedActiveFilters, sortBy, cardData, displayViewMode]);

  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  // Get view-specific configuration
  const getViewConfig = () => {
    switch (viewMode) {
      case 'evaluation':
        return {
          title: t('risk.evaluation.title'),
          description: t('risk.evaluation.description'),
          filters: { risk_status: 'IDENTIFIED' }
        };
      case 'review':
        return {
          title: t('risk.review.title'),
          description: t('risk.review.description'),
          filters: { risk_status: ['ASSIGNED', 'ACCEPTED', 'MITIGATION_PLANNED', 'MITIGATION_IN_PROGRESS'] }
        };
      case 'escalation':
        return {
          title: t('risk.escalation.title'),
          description: t('risk.escalation.description'),
          filters: { risk_status: ['MITIGATION_IN_PROGRESS', 'MITIGATION_COMPLETED'] }
        };
      case 'closure':
        return {
          title: t('risk.closure.title'),
          description: t('risk.closure.description'),
          filters: { risk_status: ['MITIGATION_COMPLETED', 'ACCEPTED'] }
        };
      default:
        return {
          title: t('risk.pool'),
          description: t('risk.pool.description'),
          filters: {}
        };
    }
  };

  const viewConfig = getViewConfig();

  // Load all options for dropdowns
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [
          categoriesData, 
          registersData,
          businessData,
          functionalData,
          geographicData,
          roadmapsData,
          strategiesData,
          transformationsData,
          processesData,
          kpisData,
          assetsData
        ] = await Promise.all([
          categoryService.getAll({ page: 1, limit: 1000 }),
          registerService.getAll({ page: 1, limit: 1000 }),
          api.get('/hierarchy/business'),
          api.get('/hierarchy/functional'),
          api.get('/hierarchy/geographic'),
          api.get('/tenant/roadmaps'),
          api.get('/generic/strategies'),
          api.get('/generic/transformations'),
          api.get('/generic/processes'),
          api.get('/generic/kpis'),
          api.get('/generic/assets')
        ]);
        
        setCategories(categoriesData.data || []);
        setRegisters(registersData.data || []);
        setBusinessHierarchies(businessData.data?.data || []);
        setFunctionalHierarchies(functionalData.data?.data || []);
        setGeographicHierarchies(geographicData.data?.data || []);
        setRoadmaps(roadmapsData.data?.data || []);
        setStrategies(strategiesData.data?.data || []);
        setTransformations(transformationsData.data?.data || []);
        setProcesses(processesData.data?.data || []);
        setKpis(kpisData.data?.data || []);
        setAssets(assetsData.data?.data || []);
      } catch (error) {
        console.error('Error loading options:', error);
      }
    };
    loadOptions();
  }, []);

  const handleCreate = () => {
    setShowCreateModal(true);
  };

  const handleEdit = (item: any) => {
    setSelectedRiskId(item.id);
    setShowDetailView(true);
    onRiskSelect?.(item);
  };

  const handleView = (item: any) => {
    setSelectedRiskId(item.id);
    setShowDetailView(true);
  };

  const handleCreateSubmit = async (data: any) => {
    setLoading(true);
    try {
      await riskPoolService.create(data);
      setShowCreateModal(false);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error creating risk:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (item: any) => {
    try {
      await riskPoolService.delete(item.id);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error deleting risk:', error);
      alert(t('risk.deleteError'));
    }
  };

  const handleDeleteWithConfirm = (item: any) => {
    if (skipDeleteConfirmation) {
      handleDelete(item);
    } else {
      setRiskToDelete(item);
    }
  };

  const handleConfirmDeleteRisk = async () => {
    if (!riskToDelete) return;
    await handleDelete(riskToDelete);
    setRiskToDelete(null);
  };

  const handleAssign = (item: any) => {
    setAssigningRisk(item);
    setShowAssignmentModal(true);
  };

  const handleAssignmentComplete = () => {
    refresh(); // Use generic refresh hook
    setShowAssignmentModal(false);
    setAssigningRisk(null);
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setShowViewModal(false);
    setViewingItem(null);
  };

  const categoryQuickFilters = React.useMemo(
    () => riskCategories.map(cat => ({ id: `category:${cat.id}`, label: cat.name, value: `category:${cat.id}` })),
    [riskCategories]
  );

  const riskQuickFilterGroups = React.useMemo(
    () => [
      {
        id: 'severity',
        label: t('risk.filters.severity') || 'Severity',
        filters: [
          { id: 'critical', label: 'Critical', value: 'critical' },
          { id: 'high', label: 'High', value: 'high' },
          { id: 'medium', label: 'Medium', value: 'medium' },
          { id: 'low', label: 'Low', value: 'low' }
        ]
      },
      {
        id: 'probability',
        label: t('risk.filters.probability') || 'Probability',
        filters: [
          { id: 'prob-low', label: 'Low', value: 'prob-low' },
          { id: 'prob-medium', label: 'Medium', value: 'prob-medium' },
          { id: 'prob-high', label: 'High', value: 'prob-high' }
        ]
      },
      {
        id: 'status',
        label: t('risk.filters.status') || 'Status',
        filters: [
          { id: 'identified', label: 'Identified', value: 'identified' },
          { id: 'assigned', label: 'Assigned', value: 'assigned' },
          { id: 'accepted', label: 'Accepted', value: 'accepted' },
          { id: 'mitigation-in-progress', label: 'In Progress', value: 'mitigation-in-progress' },
          { id: 'mitigation-completed', label: 'Completed', value: 'mitigation-completed' },
          { id: 'overdue', label: 'Overdue', value: 'overdue' }
        ]
      },
      { id: 'risk-categories', label: t('risk.filters.categories') || 'Category', filters: categoryQuickFilters }
    ],
    [categoryQuickFilters, t]
  );

  const getRiskFilterColorClass = (filter: { id: string; label: string }, isActive: boolean): string | undefined => {
    const id = String(filter.id || '').toLowerCase();
    const tone = (activeClass: string, inactiveClass: string) => (isActive ? activeClass : inactiveClass);
    const isCategory = id.startsWith('category:');
    const isLevel = ['critical', 'high', 'medium', 'low'].includes(id);
    const isProbability = ['prob-low', 'prob-medium', 'prob-high'].includes(id);
    const isStatus = ['identified', 'assigned', 'accepted', 'mitigation-in-progress', 'mitigation-completed'].includes(id);

    if (id === 'overdue') {
      return tone(
        'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-red-600 font-semibold',
        'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-red-400 hover:bg-red-50'
      );
    }
    if (isLevel) {
      if (id === 'critical') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-700 hover:bg-red-50'
        );
      }
      if (id === 'high') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-rose-600 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-rose-600 hover:bg-rose-50'
        );
      }
      if (id === 'medium') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 hover:bg-amber-50'
        );
      }
      return tone(
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 font-semibold',
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 hover:bg-emerald-50'
      );
    }
    if (isProbability) {
      if (id === 'prob-high') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-600 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-600 hover:bg-red-50'
        );
      }
      if (id === 'prob-medium') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 hover:bg-amber-50'
        );
      }
      return tone(
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 font-semibold',
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 hover:bg-emerald-50'
      );
    }
    if (isStatus) {
      if (id === 'mitigation-completed') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-green-600 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-green-400 hover:bg-green-50'
        );
      }
      if (id === 'mitigation-in-progress') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-yellow-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-yellow-300 hover:bg-yellow-50'
        );
      }
      if (id === 'identified') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-blue-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-blue-300 hover:bg-blue-50'
        );
      }
      if (id === 'assigned' || id === 'accepted') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-amber-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-amber-300 hover:bg-amber-50'
        );
      }
    }
    // Category: no background (border + text only, match other filters).
    if (isCategory) {
      return tone(
        'bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] font-semibold',
        'bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] hover:bg-[var(--color-hover-bg)]'
      );
    }
    return undefined;
  };

  return (
    <div className="risk-pool">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{viewConfig.title}</h2>
        <p className="text-sm text-gray-500">
          {viewConfig.description}
        </p>
      </div>

      <div className="mb-4">
        <FilterToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t('risk.pool.searchPlaceholder') || 'Search risks...'}
          quickFilterGroups={riskQuickFilterGroups}
          activeFilters={combinedActiveFilters}
          onFilterToggle={(filterId) => {
            if (filterId.startsWith('category:')) {
              const categoryId = filterId.replace('category:', '');
              if (categoryId) toggleCategory(categoryId);
              return;
            }
            setActiveFilters(prev =>
              prev.includes(filterId) ? prev.filter(id => id !== filterId) : [...prev, filterId]
            );
          }}
          sortOptions={[
            { id: 'relevance', label: 'Relevance', value: 'relevance' },
            { id: 'name', label: 'Name (A-Z)', value: 'name' },
            { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' },
            { id: 'priority-high', label: 'Priority (High)', value: 'priority-high' }
          ]}
          sortValue={sortBy}
          onSortChange={setSortBy}
          filterColorResolver={getRiskFilterColorClass}
          controlsAfterSort={
            <ViewSwitcher
              currentView={displayViewMode}
              onViewChange={setDisplayViewMode}
            />
          }
        />
      </div>

      {displayViewMode === 'table' ? (
        <FlexibleCRUDTable
          key={`${refreshKey}:${selectedCategoryIds.join(',')}:${activeFilters.join(',')}:${searchTerm}`}
          entity={riskEntity}
          service={riskServiceWithFilters}
          onCreate={handleCreate}
          showCreateButton={false}
          onEdit={handleEdit}
          onDelete={handleDelete}
          useConfirmDialog={true}
          skipDeleteConfirmation={skipDeleteConfirmation}
          onDeleteDontAskAgain={() => setSkipDeleteConfirmation(true)}
          customActions={[
            {
              label: t('risk.pool.assign'),
              icon: () => {
                const AssignIcon = () => (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                );
                return AssignIcon;
              },
              onClick: handleAssign,
              variant: 'primary'
            }
          ]}
          searchPlaceholder={t('risk.pool.searchPlaceholder')}
          showPagination={true}
          showSearch={false}
          onOperationComplete={() => {
            // Table will automatically refresh after operations
            // Note: This callback is only called when in table view
          }}
        />
      ) : (
        <div>
          {cardLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
            </div>
          ) : filteredCardData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                {cardData.length === 0 
                  ? 'No risks found' 
                  : 'No risks match your filters'}
              </p>
            </div>
          ) : (
            <CardGrid columns={4}>
              {filteredCardData.map((risk) => (
                <RiskCard
                  key={risk.id}
                  risk={risk}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDeleteWithConfirm}
                  customActions={[
                    {
                      label: t('risk.pool.assign'),
                      icon: () => {
                        const AssignIcon = () => (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        );
                        return AssignIcon;
                      },
                      onClick: handleAssign,
                      variant: 'primary'
                    }
                  ]}
                />
              ))}
            </CardGrid>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!riskToDelete}
        onClose={() => setRiskToDelete(null)}
        onConfirm={handleConfirmDeleteRisk}
        title={t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'}
        itemName={riskToDelete?.name || riskToDelete?.description}
        description={t('messages.deleteWarning') || 'This action cannot be undone.'}
        confirmLabel={t('common.delete') || 'Delete'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        variant="danger"
        showDontAskAgain={true}
        dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
        onDontAskAgainChange={(checked) => checked && setSkipDeleteConfirmation(true)}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <EditModal
          isOpen={showCreateModal}
          onClose={handleCancel}
          title={t('risk.pool.createTitle', { entity: riskEntity.displayNameSingular || 'Risk' })}
          entity={riskEntity}
          onSubmit={handleCreateSubmit}
          onCancel={handleCancel}
          loading={loading}
          dynamicOptions={{
            risk_category_id: categories.map(cat => ({ value: cat.id, label: cat.name })),
            risk_register_id: registers.map(reg => ({ value: reg.id, label: reg.name })),
            business_hierarchy_id: businessHierarchies.map(h => ({ value: h.id, label: h.name })),
            functional_hierarchy_id: functionalHierarchies.map(h => ({ value: h.id, label: h.name })),
            geographic_hierarchy_id: geographicHierarchies.map(h => ({ value: h.id, label: h.name })),
            roadmap_id: roadmaps.map(r => ({ value: r.id, label: r.name })),
            strategy_id: strategies.map(s => ({ value: s.id, label: s.name })),
            transformation_id: transformations.map(t => ({ value: t.id, label: t.name })),
            process_id: processes.map(p => ({ value: p.id, label: p.name })),
            kpi_id: kpis.map(k => ({ value: k.id, label: k.name })),
            asset_id: assets.map(a => ({ value: a.id, label: a.name }))
          }}
        />
      )}

      {/* View Modal */}
      {showViewModal && viewingItem && (
        <ViewModal
          isOpen={showViewModal}
          onClose={handleCancel}
          title={`View ${riskEntity.displayNameSingular}`}
          data={viewingItem}
          fields={riskEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}

      {/* Risk Detail View */}
      {showDetailView && selectedRiskId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <RiskDetailView
              riskId={selectedRiskId}
              onClose={() => {
                setShowDetailView(false);
                setSelectedRiskId(null);
                // Clear any riskId from URL
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('riskId');
                setSearchParams(newSearchParams, { replace: true });
                refresh(); // Use generic refresh hook
              }}
            />
          </div>
        </div>
      )}

      {/* Risk Assignment Modal */}
      {showAssignmentModal && assigningRisk && (
        <RiskAssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false);
            setAssigningRisk(null);
          }}
          risk={assigningRisk}
          onAssignmentComplete={handleAssignmentComplete}
        />
      )}
    </div>
  );
};
